import { FarmWithStakedValue } from '@pancakeswap/farms'
import { useTranslation } from '@pancakeswap/localization'
import { Card, ExpandableSectionButton, Flex, FlexGap, Skeleton, Text, useModalV2 } from '@pancakeswap/uikit'
import { FarmWidget } from '@pancakeswap/widgets-internal'
import BigNumber from 'bignumber.js'
import { BASE_ADD_LIQUIDITY_URL } from 'config'
import { CHAIN_QUERY_NAME } from 'config/chains'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useCallback, useMemo, useState } from 'react'
import useRowReveal from 'hooks/useRowReveal'
import { multiChainPaths } from 'state/info/constant'
import { useLPAprWithAddress } from 'state/swap/useLPApr'
import { styled } from 'styled-components'
import { getBlockExploreLink } from 'utils'
import getLiquidityUrlPathParts from 'utils/getLiquidityUrlPathParts'
import { getDisplayApr } from 'views/Farms/components/getDisplayApr'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { AddLiquidityV3Modal } from 'views/AddLiquidityV3/Modal'
import { SELECTOR_TYPE } from 'views/AddLiquidityV3/types'
import { useFarmV2Multiplier } from 'views/Farms/hooks/useFarmV2Multiplier'
import ApyButton from './ApyButton'
import BoostedApr from '../YieldBooster/components/BoostedApr'
import { formatAmount } from '../../../../utils/formatInfoNumbers'
import CardActionsContainer from './CardActionsContainer'
import CardHeading from './CardHeading'

const { DetailsSection } = FarmWidget.FarmCard

const StyledCard = styled(Card)`
  align-self: baseline;
  max-width: 100%;
  margin: 0 0 24px 0;
  transition: all 200ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(139, 92, 246, 0.4);
  }

  hr {
    width: 100%;
    border-color: ${({ theme }) => theme.colors.cardBorder};
    margin-block: 1em;
  }
  
  ${({ theme }) => theme.mediaQueries.sm} {
    max-width: 350px;
    margin: 0 12px 46px;
  }
`

const FarmCardInnerContainer = styled(Flex)`
  flex-direction: column;
  justify-content: space-around;
  padding: 24px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  // background: rgba(11, 5, 8, 0.2);  
`

const ExpandingWrapper = styled.div`
  padding: 24px;
  padding-top: 0;
  // border-top: 2px solid ${({ theme }) => theme.colors.cardBorder};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  // background: rgba(11, 5, 8, 0.2);
`

interface FarmCardProps {
  farm: FarmWithStakedValue
  displayApr: string
  removed: boolean
  cakePrice?: BigNumber
  account?: string
  originalLiquidity?: BigNumber
}

const FarmCard: React.FC<FarmCardProps> = ({
  farm,
  displayApr,
  removed,
  cakePrice,
  account,
  originalLiquidity,
}) => {
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()
  const [showExpandableSection, setShowExpandableSection] = useState(false)

  const { totalMultipliers, getFarmCakePerSecond } = useFarmV2Multiplier()

  const farmCakePerSecond = getFarmCakePerSecond(farm.poolWeight)
  


  const liquidity =
    farm?.liquidity && originalLiquidity?.gt(0) ? farm.liquidity.plus(originalLiquidity) : farm.liquidity

  const totalValueFormatted =
    liquidity && liquidity.gt(0)
      ? `$${liquidity.toNumber().toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : ''
  
  const token0 = unwrappedToken(farm.quoteToken)
  const token1 = unwrappedToken(farm.token)

  const lpLabel = `${token0.symbol}-${token1.symbol}`
  const earnLabel = farm.dual ? farm.dual.earnLabel : t('BBC + Fees')

  const liquidityUrlPathParts = getLiquidityUrlPathParts({
    quoteTokenAddress: farm.quoteToken.address,
    tokenAddress: farm.token.address,
    chainId,
  })
  const addLiquidityUrl = `${BASE_ADD_LIQUIDITY_URL}/v2/${liquidityUrlPathParts}&v2=true`
  const { lpAddress, stableSwapAddress, stableLpFee } = farm
  const isPromotedFarm = farm.token.symbol === 'BBC'

  const infoUrl = useMemo(() => {
    if (farm.isStable) {
      return `/info/pairs/${stableSwapAddress}?type=stableSwap&chain=${CHAIN_QUERY_NAME[chainId]}`
    }
    return `/info/pairs/${lpAddress}?chain=${CHAIN_QUERY_NAME[chainId]}`
  }, [chainId, farm.isStable, lpAddress, stableSwapAddress])

  const toggleExpandableSection = useCallback(() => {
    setShowExpandableSection((prev) => !prev)
  }, [])

  const addLiquidityModal = useModalV2()


  const poolData = useLPAprWithAddress(lpAddress, chainId)
  const lpRewardsApr = formatAmount(poolData?.lpApr7d??0)

  const revealRef = useRowReveal<HTMLDivElement>()

  return (
    <StyledCard isActive={isPromotedFarm}>
      <FarmCardInnerContainer ref={revealRef}>
        <CardHeading
          lpLabel={lpLabel}
          multiplier={farm.multiplier}
          isCommunityFarm={farm.isCommunity}
          token={farm.token}
          quoteToken={farm.quoteToken}
          boosted={false}
          isStable={farm.isStable}
          version={2}
          pid={farm.pid}
          farmCakePerSecond={farmCakePerSecond}
          totalMultipliers={totalMultipliers}
        />
        <Flex justifyContent="space-between">
        {!removed && (
          <Flex flexDirection="column" >
            <Text color="textSubtle" fontSize={16}>{t('APR')}</Text>
            <Flex alignItems="center">
              {farm.apr != null ? (
                <>
                     {/* {farm.boosted ? (
                    <BoostedApr
                      mr="4px"
                      lpRewardsApr={farm.lpRewardsApr}
                      apr={farm.apr}
                      pid={farm?.pid}
                      lpTotalSupply={farm.lpTotalSupply}
                      userBalanceInFarm={
                        (stakedBalance.plus(tokenBalance).gt(0)
                          ? stakedBalance?.plus(tokenBalance)
                          : proxy?.stakedBalance.plus(proxy?.tokenBalance)) ?? BIG_ZERO
                      }
                    />
                  ) : null} */}
                  <ApyButton
                    variant="text-and-button"
                    pid={farm.pid}
                    lpTokenPrice={farm.lpTokenPrice}
                    lpSymbol={farm.lpSymbol}
                    multiplier={farm.multiplier}
                    lpLabel={lpLabel}
                    addLiquidityUrl={addLiquidityUrl}
                    cakePrice={cakePrice}
                    apr={farm.apr}
                    displayApr={farm.apr !== 0 ? getDisplayApr(farm.apr, Number(lpRewardsApr)) : '0'}
                    lpRewardsApr={Number(lpRewardsApr)}
                    strikethrough={false}
                    useTooltipText
                    boosted={false}
                    stableSwapAddress={stableSwapAddress}
                    stableLpFee={stableLpFee}
                    farmCakePerSecond={farmCakePerSecond}
                    totalMultipliers={totalMultipliers}
                  />
                </>
              ) : (
                <Skeleton height={24} width={80} />
              )}
            </Flex>
          </Flex>
        )}
          <Flex flexDirection="column" >
            <Text color="textSubtle" fontSize={16}>{t('Earn')}</Text>
            <Text>{earnLabel}</Text>
          </Flex>
        </Flex>
        <hr />
        <CardActionsContainer
          farm={farm}
          lpLabel={lpLabel}
          account={account}
          addLiquidityUrl={addLiquidityUrl}
          displayApr={displayApr}
        />
      </FarmCardInnerContainer>

      <ExpandingWrapper>
        <ExpandableSectionButton onClick={toggleExpandableSection} expanded={showExpandableSection} />
        {showExpandableSection && (
          <>
            {/* <AddLiquidityV3Modal
              {...addLiquidityModal}
              currency0={token0}
              currency1={token1}
              preferredSelectType={farm.isStable ? SELECTOR_TYPE.STABLE : SELECTOR_TYPE.V2}
            /> */}
            <DetailsSection
              removed={removed}
              scanAddress={{ link: getBlockExploreLink(lpAddress, 'address', chainId), chainId }}
              infoAddress={infoUrl}
              totalValueFormatted={totalValueFormatted}
              lpLabel={lpLabel}
              onAddLiquidity={`/add/${farm.token.address}/${farm.quoteToken.address}?v2=true`}
              isCommunity={farm.isCommunity}
              auctionHostingEndDate={farm.auctionHostingEndDate}
              multiplier={farm.multiplier}
              farmCakePerSecond={farmCakePerSecond}
              totalMultipliers={totalMultipliers}
            />
          </>
        )}
      </ExpandingWrapper>
    </StyledCard>
  )
}

export default FarmCard
